// @ts-nocheck
'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** cn_wall :
//***********************************************************************************
//***********************************************************************************

import {
    cn_add,
    cn_box,
    cn_clone,
    cn_clone_3d,
    cn_dist,
    cn_dot,
    cn_intersect_line,
    cn_max,
    cn_md5,
    cn_middle,
    cn_min,
    cn_mul,
    cn_normal,
    cn_normalize,
    cn_polar,
    cn_project_segment,
    cn_sub,
    cn_translate_polygon,
    cnx_add,
    cnx_clone,
    cnx_mul
} from '../utils/cn_utilities';
import { cn_element_visitor } from '../utils/visitors/cn_element_visitor';
import { cn_balcony_type } from './cn_balcony_type';
import { cn_element } from './cn_element';
import { cn_opening } from './cn_opening';
import { cn_niche } from './cn_niche';
import { cn_wall_type } from './cn_wall_type';
import { cn_fence_type } from './cn_fence_type';
import { cn_vertex } from './cn_vertex';
import { fh_add, fh_clone, fh_mul, fh_polygon, fh_solid, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_wall_interface } from './cn_wall_interface';
import { CN_WALL_COSINE_SHAPE_THRESHOLD, CN_WALL_SINE_SHAPE_THRESHOLD } from './cn_model_constants';
import { logger } from '../utils/cn_logger';
import { cn_plugin_option } from '../utils/cn_plugin_option';
import { cn_transaction_manager } from '../utils/cn_transaction_manager';
// @ts-ignore

export const CN_MIDDLE = 0;
export const CN_OUTER = 1;
export const CN_INNER = 2;

export function cn_reverse_axis(axis) {
    if (axis > 0) return 3 - axis;
    return 0;
}

export const CN_WALL_DRAW_MODE_RECTANGLE = 0;
export const CN_WALL_DRAW_MODE_OPEN_POLYGON = 1;

function sort_openings_by_position(o0, o1) {
    if (o0.position < o1.position) return -1;
    return 1;
}

function sort_niches_by_position(o0, o1) {
    if (o0.wall_position < o1.wall_position) return -1;
    return 1;
}

//***********************************************************************************
/**
 * cn_wall_shape_impact : a raytracing impact on the shape of a wall.
 * Contains a list of openings that at leat partially match the interface.
 */
//***********************************************************************************
export class cn_wall_shape_impact {
    /**
     *
     * @param {cn_wall} wall
     * @param {number} side
     * @param {number} distance
     * @param {number[]} point
     * @param {number[]} normal
     * @param {number} abcissa
     */
    constructor(wall, side, distance, point, normal, abcissa) {
        this.wall = wall;
        this.distance = distance;
        this.point = point;
        this.normal = normal;
        this.abcissa = abcissa;
        this.side = side;
    }
}

export class cn_wall_upper_part {
    constructor(z0, z1, wall, wall_type) {
        this.z0 = z0;
        this.z1 = z1;
        this.wall = wall;
        this.wall_type = wall_type;
    }
}
export class cn_wall extends cn_element {
    constructor(v0, v1, wt, axis, scene) {
        super(scene);
        this.class_name = 'cn_wall';
        this.scene = scene;

        //*** Model data
        this.vertices = [v0, v1];
        this.balcony = (wt && wt.constructor == cn_balcony_type);
        this.fence = (wt && wt.constructor == cn_fence_type);
        this.axis = axis;
        this.wall_type = wt;
        this.openings = [];
        this.niches = [];
        this.flip_wall_type = false;

        this.supported_vertices = [];

        //*** Data for other wall types */
        this.upper_wall_types = [];
        this.upper_heights = [];

        //*** volatile data
        this.draw_priority = 5;
        this.shape = [[0, 0], [0, 0], [0, 0], [0, 0]];
        this.valid = true;
        this.contours = [null, null];
        this.single = [false, false];
        this.space_lines = [[], []];
        this.full_space_lines = [[], []];
        this.measure_points = [[[0, 0], [0, 0]], [[0, 0], [0, 0]]];
        this.facing_topo = [false, false];
        this.dormer = null;
        this.s_index = 0;

        /** @type {fh_polygon} */
        this.footprint = null;

        /** @type {import('./cn_space_segment').cn_space_segment[]} */
        this.space_segments = [];

        /** @type {cn_wall_interface[]} */
        this.wall_interfaces = [];
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize_with_selection(check_selection) {
        return this._serialize(check_selection);
    }

    serialize() {
        return this._serialize(false);
    }

    _serialize(check_selection) {
        var json = this.serialize_element();
        json.locked = this.locked;
        json.vertices = [this.vertices[0].s_index, this.vertices[1].s_index];
        json.balcony = this.balcony;
        json.fence = this.fence;
        json.axis = this.axis;
        json.wall_type = this.wall_type.ID;
        json.flip_wall_type = this.flip_wall_type;
        json.openings = [];
        json.upper_wall_types = this.upper_wall_types.map(wt => wt.ID);
        json.upper_heights = this.upper_heights;
        for (var i in this.openings) {
            if (check_selection && !this.openings[i].selected) continue;
            json.openings.push(this.openings[i].serialize());
        }
        json.niches = [];
        for (var i in this.niches) {
            if (check_selection && !this.niches[i].selected) continue;
            json.niches.push(this.niches[i].serialize());
        }

        json.wall_interfaces = this.wall_interfaces.map(wi => wi.serialize());

        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json) != 'object') return false;
        if (typeof (json.vertices) != 'object') return false;
        if (typeof (json.openings) != 'object') return false;

        if (json.vertices[0] == json.vertices[1]) return false;

        var v0 = scene.vertices[json.vertices[0]];
        var v1 = scene.vertices[json.vertices[1]];
        if (!v0 || !v1) return false;

        var balcony = (typeof (json.balcony) == 'boolean') ? json.balcony : false;
        var fence = (typeof (json.fence) == 'boolean') ? json.fence : false;

        var wt = null;
        if (typeof (json.wall_type) == 'string')
            wt = scene.building.get_element_type(json.wall_type);
        if (wt == null) {
            logger.log('use default wall type');
            if (balcony)
                wt = scene.building.get_balcony_types()[0];
            else if (fence)
                wt = scene.building.get_fence_types()[0];
            else
                wt = scene.building.get_wall_types()[0];
        }
        if (balcony && wt.constructor != cn_balcony_type)
            logger.log('INCOHERENCE !!!', json, wt, scene.building);

        if (fence && wt.constructor != cn_fence_type)
            logger.log('INCOHERENCE !!!', json, wt, scene.building);

        if (!balcony && !fence && wt.constructor != cn_wall_type)
            logger.log('INCOHERENCE !!!', json, wt, scene.building);

        var wall = new cn_wall(v0, v1, wt, CN_MIDDLE, scene);
        wall.unserialize_element(json);

        if (typeof (json.axis) == 'number')
            wall.axis = json.axis;

        if (typeof (json.locked) == 'boolean')
            wall.locked = json.locked;

        if (typeof (json.flip_wall_type) == 'boolean')
            wall.flip_wall_type = json.flip_wall_type;

        for (var i in json.openings) {
            var opening = cn_opening.unserialize(json.openings[i], scene);
            if (!opening) {
                logger.log('WARNING : cannot unserialize opening : ', json.openings[i]);
                continue;
            }
            opening.wall = wall;
            wall.openings.push(opening);
        }

        //*** read niches */
        for (var i in json.niches) {
            var niche = cn_niche.unserialize(json.niches[i], scene);
            if (!niche) {
                logger.log('WARNING : cannot unserialize niche : ', json.niches[i]);
                continue;
            }
            niche.wall = wall;
            wall.niches.push(niche);
        }

        //*** wall interfaces */
        if (json.wall_interfaces)
            wall.wall_interfaces = json.wall_interfaces.map(jwi => cn_wall_interface.unserialize(jwi, wall));

        //*** OBSOLETE delegate management */
        if (typeof (json.delegates) == 'object' && json.delegates && json.delegates.length >= 2) {
            for (var vi = 0; vi < 2; vi++) {
                if (json.delegates[vi] && json.delegates[vi].position) {
                    wall.vertices[vi] = new cn_vertex(json.delegates[vi].position, false, scene);
                    scene.vertices.push(wall.vertices[vi]);
                }
            }
        }

        //*** OBSOLETE facing management */
        if (json.facings && json.facings.length) {
            wall.facings = json.facings.map(facing => facing && facing.ID ? scene.building.facing_types.find(ft => ft.ID === facing.ID) : null);
        }

        if (cn_plugin_option.vertical_walls) {
            if (json.upper_wall_types)
                wall.upper_wall_types = json.upper_wall_types.map(id => scene.building.get_element_type(id)).filter(wt => !!wt);
            if (json.upper_heights)
                wall.upper_heights = json.upper_heights;
            while (wall.upper_heights.length < wall.upper_wall_types.length) wall.upper_wall_types.splice(wall.upper_wall_types.length - 1, 1);
            while (wall.upper_wall_types.length < wall.upper_heights.length) wall.upper_heights.splice(wall.upper_heights.length - 1, 1);
        }
        scene.walls.push(wall);
        wall.parent = scene;
        return wall;
    }

    /**
     * Regenerates the ID of the element.
     * TODO : also regenerate descendants in derivate methods.
     */
    regenerate_id() {
        super.regenerate_id();
        this.openings.forEach(op => op.regenerate_id());
    }

    /**
     * Checks unicity of IDS inside a tree of elements.
     * @param {string[]} id_list
     */
    check_id(id_list) {
        super.check_id(id_list);
        this.openings.forEach(op => op.check_id(id_list));
    }

    is_wall_dormer() {
        return this.dormer != null;
    }

    /**
     * returns vertex position
     * @param {*} index
     */
    vertex_position(index) {
        return cn_clone(this.vertices[index].position);
    }

    /**
     * Returns the direction of the wall, starting at given vertex.
     * @param {cn_vertex} vertex
     * @returns {number[]}
     */
    direction_from(vertex) {
        const wid = (this.vertices[0] == vertex) ? 0 : 1;
        const dir = cn_sub(this.vertex_position(1 - wid), this.vertex_position(wid));
        cn_normalize(dir);
        return dir;
    }

    /**
     * Returns the direction of the wall, ending at given vertex.
     * @param {cn_vertex} vertex
     * @returns {number[]}
     */
    direction_to(vertex) {
        const wid = (this.vertices[0] == vertex) ? 0 : 1;
        const dir = cn_sub(this.vertex_position(wid), this.vertex_position(1 - wid));
        cn_normalize(dir);
        return dir;
    }

    dummy_clone(wall_type) {
        let v0 = this.vertices[0].dummy_clone();
        let v1 = this.vertices[1].dummy_clone();
        let new_wall = new cn_wall(v0, v1, wall_type, this.axis, null);
        for (var nv = 0; nv < 2; nv++) {
            let vertex = new_wall.vertices[nv];
            let wi = vertex.walls.indexOf(this);
            vertex.walls[wi] = new_wall;
        }
        return new_wall;
    }

    /**
     * returns a point and direction of the given side, by index.
     * Sides are like this :
     *                         side 1
     *              |-----------------------------|
     *        side 2  |                             |  side 3
     *              |-----------------------------|
     *                           side 0
     *
     * @param {number} side
     * @returns {number[][]}
     */
    get_side(side) {
        if (side == 0)
            return [this.bounds.pmin, this.bounds.direction];
        if (side == 1)
            return [this.bounds.pmax, this.bounds.direction];
        if (side == 2)
            return [this.vertex_position(0), this.bounds.normal];
        return [this.vertex_position(1), this.bounds.normal];
    }

    get_side_segment(side) {
        if (side == 0)
            return [this.shape[0], this.shape[3]];
        if (side == 1)
            return [this.shape[2], this.shape[1]];
        if (side == 2)
            return [this.shape[1], this.shape[0]];
        return [this.shape[3], this.shape[2]];
    }

    /**
     * Finds at which index a given vertex lies.
     * @param {cn_vertex} v
     * @returns {number}
     */
    vertex_index(v) {
        if (this.vertices[0] == v) return 0;
        if (this.vertices[1] == v) return 1;
        logger.error("We shouldn't be here");
        return -1;
    }

    //***********************************************************************************
    //**** Topological operations
    //***********************************************************************************
    /**
     * Disconnect a vertex from this
     * @param {cn_vertex} vertex
     * @param {cn_transaction_manager} transaction_manager
     */
    disconnect_vertex(vertex, transaction_manager) {
        const index = this.supported_vertices.indexOf(vertex);
        if (index < 0) return;
        if (transaction_manager) transaction_manager.push_item_set(this, "supported_vertices");
        this.supported_vertices.splice(index, 1);
    }

    /**
     * Connects a vertex to this
     * @param {cn_vertex} vertex
     * @param {cn_transaction_manager} transaction_manager
     */
    connect_vertex(vertex, transaction_manager) {
        if (this.supported_vertices.includes(vertex)) return;
        if (transaction_manager) transaction_manager.push_item_set(this, "supported_vertices");
        this.supported_vertices.push(vertex);
    }

    //***********************************************************************************
    //**** Build lines
    //***********************************************************************************
    //*** Build geometry of bounds of the wall
    build_self() {
        var bounds = {};
        var v0 = this.vertex_position(0);
        var v1 = this.vertex_position(1);
        bounds.direction = cn_sub(v1, v0);
        bounds.length = cn_normalize(bounds.direction);
        bounds.normal = cn_normal(bounds.direction);

        bounds.y0 = this.compute_y(this.axis);
        bounds.y1 = bounds.y0 + this.wall_type.thickness;
        bounds.pmin = cn_add(v0, cn_mul(bounds.normal, bounds.y0));
        bounds.pmax = cn_add(v0, cn_mul(bounds.normal, bounds.y1));
        this.bounds = bounds;
        this.valid = (bounds.length > 0.01);
        this.opening_area = [0, this.bounds.length];
        this.shape = [bounds.pmin, bounds.pmax, cn_add(bounds.pmax, cn_mul(bounds.direction, bounds.length)), cn_add(bounds.pmin, cn_mul(bounds.direction, bounds.length))];
        this.single = [false, false];
        this.space_lines = [[cn_clone(this.shape[0]), cn_clone(this.shape[3])], [cn_clone(this.shape[2]), cn_clone(this.shape[1])]];
        this.full_space_lines = [[cn_clone(this.shape[0]), cn_clone(this.shape[3])], [cn_clone(this.shape[2]), cn_clone(this.shape[1])]];
    }

    compute_y(axis) {
        if (axis == CN_OUTER)
            return -this.wall_type.thickness;
        else if (axis == CN_INNER)
            return 0;
        return -0.5 * this.wall_type.thickness;
    }

    /**
     * Returns the abcissa of a point along the wall.
     * @param {number[]} p
     * @returns {number}
     */
    compute_abcissa(p) {
        return cn_dot(this.bounds.direction, cn_sub(p, this.bounds.pmin));
    }

    /**
     * Returns the closest side of a point along the wall.
     * @param {number[]} p
     * @returns {number}
     */
    compute_side(p) {
        let y = cn_dot(this.bounds.normal, cn_sub(p, this.bounds.pmin));
        if (y < 0.5 * this.wall_type.thickness) return 0;
        return 1;
    }

    /**
     * Computes axis position from abcissa
     * @param {number} a
     * @returns {number[]}
     */
    compute_axis_position_from_abcissa(a) {
        return cn_add(this.vertices[0].position, cn_mul(this.bounds.direction, a));
    }

    /**
     * Computes axis position from abcissa
     * @param {number} a
     * @returns {number[]}
     */
    compute_side_position_from_abcissa(a, side) {
        return cn_add(this.compute_axis_position_from_abcissa(a), cn_mul(this.bounds.normal, (side == 0) ? this.bounds.y0 : this.bounds.y1));
    }

    /**
     * Adjust the shape of the wall to fit a given line
     * @param {number} vertex_index
     */
    adjust_shape(vertex_index) {
        const plane = this.vertices[vertex_index].compute_cutting_plane();
        if (plane) {
            const point = plane.position;
            const normal = plane.normal;
            const direction = this.bounds.direction;
            const pscal = cn_dot(normal, direction);
            if (Math.abs(pscal) > CN_WALL_SINE_SHAPE_THRESHOLD) {
                const p0 = this.shape[(vertex_index == 1) ? 0 : 3];
                const x0 = cn_dot(normal, cn_sub(point, p0)) / pscal;
                this.shape[(vertex_index == 1) ? 3 : 0] = cn_add(p0, cn_mul(direction, x0));
                const p1 = this.shape[(vertex_index == 1) ? 1 : 2];
                const x1 = cn_dot(normal, cn_sub(point, p1)) / pscal;
                this.shape[(vertex_index == 1) ? 2 : 1] = cn_add(p1, cn_mul(direction, x1));
                return;
            }
        }
        if (vertex_index == 0) {
            this.shape[0] = this.bounds.pmin;
            this.shape[1] = this.bounds.pmax;
        }
        else {
            this.shape[2] = cn_add(this.bounds.pmax, cn_mul(this.bounds.direction, this.bounds.length));
            this.shape[3] = cn_add(this.bounds.pmin, cn_mul(this.bounds.direction, this.bounds.length));
        }
    }

    /**
     * Adjust the shapes of the wall to fit a given line
     */
    adjust_shapes() {
        this.adjust_shape(0);
        this.adjust_shape(1);
        this.update_openings();
    }
    /**
     * Finds where this wall's vertex given by vertex_index could lie on the wall in input.
     * @param {number} vertex_index
     * @param {cn_wall} wall
     * @returns
     */
    find_connection(vertex_index, wall) {
        var best_impact = null;
        const p0 = this.vertex_position(1 - vertex_index);
        const dir = (vertex_index) ? this.bounds.direction : cn_mul(this.bounds.direction, -1);
        const sh0 = this.shape[(vertex_index) ? 0 : 3];
        const sh1 = this.shape[(vertex_index) ? 1 : 2];

        //*** we make 3 impacts. the first one is along the middle axis, the two other ones are on the sides of the wall */
        for (var niter = 0; niter < 3; niter++) {
            let origin = [0, 0];
            if (niter == 0)
                origin = cn_middle(sh0, sh1)
            else if (niter == 1)
                origin = sh0;
            else
                origin = sh1;
            const impact = wall.raytrace_shape(origin, dir);
            if (!impact) continue;

            //*** if impact is found on the axis, we're good */
            if (niter == 0 || best_impact == null) {
                best_impact = impact;
                if (niter != 1) break;
            }
            else {
                const x0 = cn_dot(best_impact.normal, cn_sub(best_impact.point, p0)) / cn_dot(dir, best_impact.normal);
                const x1 = cn_dot(impact.normal, cn_sub(impact.point, p0)) / cn_dot(dir, impact.normal);
                if (x1 < x0) best_impact = impact;
            }
        }

        if (!best_impact) return null;

        const p1 = this.vertex_position(vertex_index);
        const x = cn_dot(best_impact.normal, cn_sub(best_impact.point, p1)) / cn_dot(dir, best_impact.normal);
        return {
            point: cn_add(p1, cn_mul(dir, x)),
            offset: x,
            side: best_impact.side,
            normal: best_impact.normal
        };
    }

    /**
     * Build interfaces, i.e. couples of space segments that face each other.
     */
    build_wall_interfaces() {
        let old_wall_interfaces = this.wall_interfaces;

        const segments0 = this.space_segments.filter(ss => ss.wall_side == 0).sort((a, b) => a.wall_positions[0] - b.wall_positions[0]);
        const segments1 = this.space_segments.filter(ss => ss.wall_side == 1).sort((a, b) => a.wall_positions[0] - b.wall_positions[0]);
        this.wall_interfaces = [];
        let wi = null;

        //*** simple case : force wall interface */
        if (segments0.length == 1 && segments1.length == 1) {
            wi = new cn_wall_interface(this);
            wi.space_segments[0] = segments0[0];
            wi.space_segments[1] = segments1[0];
            wi.spaces[0] = segments0[0].space;
            wi.spaces[1] = segments1[0].space;
            wi["short"] = false;
            this.wall_interfaces.push(wi);
        }
        else {
            segments0.forEach((ss0, i0) => {
                const x0 = ss0.wall_positions[0];
                const x1 = ss0.wall_positions[1];
                segments1.forEach((ss1, i1) => {
                    const xx0 = ss1.wall_positions[1];
                    const xx1 = ss1.wall_positions[0];
                    if (xx0 <= x1 && xx1 >= x0) {
                        wi = new cn_wall_interface(this);
                        wi.space_segments[0] = ss0;
                        wi.space_segments[1] = ss1;
                        wi.ID = 'cn' + cn_md5(ss0.ID, ss1.ID);
                        wi.spaces[0] = ss0.space;
                        wi.spaces[1] = ss1.space;
                        wi["short"] = (Math.min(x1, xx1) - Math.max(x0, xx0) < this.wall_type.thickness);
                        this.wall_interfaces.push(wi);
                    }
                });
            });
        }

        function skip_wi(wi0, wi1, wi2) {
            if (!wi0 || !wi1 || !wi2) return false;
            if (wi0.space_segments[0] == wi1.space_segments[0] && wi1.space_segments[1] == wi2.space_segments[1]) return true;
            if (wi0.space_segments[1] == wi1.space_segments[1] && wi1.space_segments[0] == wi2.space_segments[0]) return true;
            return false;
        }

        this.wall_interfaces = this.wall_interfaces.filter((wi, i) => !wi["short"] || !skip_wi(this.wall_interfaces[i - 1], wi, this.wall_interfaces[i + 1]));

        //*** Build wall interfaces geometries */
        let sh0 = this.shape[0];
        let sh1 = this.shape[1];
        this.wall_interfaces.forEach((wi, ii) => {
            wi.shape[0] = sh0;
            wi.shape[1] = sh1;
            if (ii == this.wall_interfaces.length - 1) {
                wi.shape[2] = this.shape[2];
                wi.shape[3] = this.shape[3];
            }
            else {
                const win = this.wall_interfaces[ii + 1];
                let a0 = NaN;
                let a1 = NaN;
                if (win.space_segments[0] != wi.space_segments[0])
                    a0 = 0.5 * (wi.space_segments[0].wall_positions[1] + win.space_segments[0].wall_positions[0]);
                if (win.space_segments[1] != wi.space_segments[1])
                    a1 = 0.5 * (wi.space_segments[1].wall_positions[0] + win.space_segments[1].wall_positions[1]);
                if (isNaN(a0)) a0 = a1;
                if (isNaN(a1)) a1 = a0;
                sh0 = this.compute_side_position_from_abcissa(a0, 0);
                sh1 = this.compute_side_position_from_abcissa(a1, 1);
                wi.shape[2] = sh1;
                wi.shape[3] = sh0;
            }
            wi.footprint = new fh_polygon([0, 0, 0], [0, 0, 1]);
            wi.footprint.add_contour(wi.shape.map(v => cnx_clone(v)));
            wi.footprint.intersects(this.footprint);

            wi.wall_positions[0] = Math.max(this.compute_abcissa(wi.shape[0]), this.compute_abcissa(wi.shape[1]));
            wi.wall_positions[1] = Math.min(this.compute_abcissa(wi.shape[2]), this.compute_abcissa(wi.shape[3]));

            wi.space_ids = wi.spaces.map(s => s.outside ? "" : s.ID);
        });

        //*** Concile wall interfaces */
        if (old_wall_interfaces.length) {
            if (old_wall_interfaces.length == this.wall_interfaces.length) {
                for (var i = 0; i < old_wall_interfaces.length; i++)
                    this.wall_interfaces[i].copy_parameters(old_wall_interfaces[i]);
            }
            else {
                this.wall_interfaces.forEach(wi => {
                    let x0 = wi.wall_positions[0];
                    let x1 = wi.wall_positions[1];
                    let best_score = 0;
                    let best_candidate = null;
                    old_wall_interfaces.forEach(c => {
                        let xx0 = c.wall_positions[0];
                        let xx1 = c.wall_positions[1];
                        let score = Math.min(x1, xx1) - Math.max(x0, xx0);
                        if (score > best_score) {
                            score = best_score;
                            best_candidate = c;
                        }
                    });
                    if (best_candidate)
                        wi.copy_parameters(best_candidate, false);
                });
            }
        }

        this.update_openings();
    }

    /**
     * Return wall type and upper wall types
     * @returns {import('./cn_wall_type').cn_wall_type[]}
     */
    get_wall_types() {
        //*** This manipulation removes all elements that are here twice */
        let set = new Set([this.wall_type, ...this.upper_wall_types]);
        return [...set];
    }

    get_upper_parts() {
        if (this.upper_wall_types.length) {
            let res = [];
            res.push(new cn_wall_upper_part(-1000, 1000, this, this.wall_type));
            res = res.concat(this.upper_wall_types.map(uwt => new cn_wall_upper_part(-1000, 1000, this, uwt)));
            this.upper_heights.forEach((h, i) => {
                res[i].z1 = h;
                res[i + 1].z0 = h;
            });
            return res;
        }
        return new cn_wall_upper_part(-1000, 1000, this, this.wall_type);
    }

    get_neighbour_upper_parts(vertex_index, upper_part) {
        let res = [];
        let z0 = (upper_part > 0) ? this.upper_heights[upper_part - 1] : -1000;
        let z1 = (upper_part < this.upper_heights.length) ? this.upper_heights[upper_part] : 1000;
        if (vertex_index >= 0) {
            let vertex = this.vertices[vertex_index];
            let upper_parts = [];
            if (vertex.support_wall)
                upper_parts = vertex.support_wall.get_upper_parts();
            else if (vertex.walls.length == 2)
                upper_parts = vertex.walls[1 - vertex.walls.indexOf(this)].get_upper_parts();
            while (upper_parts.length && upper_parts[0].z1 <= z0)
                upper_parts.splice(0, 1);
            while (upper_parts.length && upper_parts[upper_parts.length - 1].z0 >= z1)
                upper_parts.splice(upper_parts.length - 1, 1);
            if (upper_parts.length) {
                if (upper_parts[0].z0 <= z0)
                    upper_parts[0].z0 = z0;
                else
                    upper_parts.unshift(new cn_wall_upper_part(z0, upper_parts[0].z0, null, null));
                upper_parts[upper_parts.length - 1].z1 = z1;
                return upper_parts;
            }
        }
        return [new cn_wall_upper_part(z0, z1, this, this.wall_type)];
    }

    /**
     * concile facings on space segments
     */
    concile_facings(old_space_segments) {

        //*** Manage old elements */
        if (this.facings) {
            this.space_segments.forEach(ss => {
                if (ss.wall_side == 0) ss.facing = this.facings[0];
                else if (ss.wall_side == 1) ss.facing = this.facings[1];
            });
            this.facings = undefined;
            return;
        }

        if (!old_space_segments || old_space_segments.length == 0) return;

        //*** loop on sides and spaces */
        for (var side = 0; side < 4; side++) {
            //*** Expect a case where we have some datéa on old and new space segments */
            const new_ss = this.space_segments.filter(ss => ss.wall_side == side);
            if (!new_ss.length) continue;
            const old_ss = old_space_segments.filter(ss => ss.wall_side == side);
            if (!old_ss.length) continue;

            //*** Concile moldings */
            new_ss.forEach(ss => ss.concile_moldings(old_ss));

            //*** Maybe one single facing? */
            if (old_ss.length == 1 || !old_ss.some(ss => !ss.same_facings(old_ss[0]))) {
                new_ss.forEach(ss => ss.copy_facings(old_ss[0]));
                continue;
            }
            if (side >= 2) continue;

            //*** trys to concile the best we can */
            new_ss.forEach(nss => {
                let best_length = 0;
                const x0 = nss.wall_positions[side];
                const x1 = nss.wall_positions[1 - side];
                old_ss.forEach(oss => {
                    const y0 = oss.wall_positions[side];
                    const y1 = oss.wall_positions[1 - side];
                    if (y0 > x1 || y1 < x0) return;
                    let length = x1 - x0;
                    if (y0 > x0) length -= y0 - x0;
                    if (y1 < x1) length -= x1 - y1;
                    if (length > best_length) {
                        nss.copy_facings(oss);
                        best_length = length;
                    }
                });
            });
        }
    }

    //*** build lines of the wall
    //***      pmax  shape[1]                      shape[2]
    //***      V0.............................................v1
    //***      pmin  shape[0]                      shape[3]
    build_bounds(vertex_index) {
        if (!this.valid) return;

        //*** Special case : the wall is single. default bounds are ok. */
        this.single[vertex_index] = (this.vertices[vertex_index].previous_wall(this, false) == this);
        if (this.single[vertex_index]) return false;

        var vertex = this.vertices[vertex_index];
        var vertex_position = this.vertex_position(vertex_index);
        for (var niter = 0; niter < 2; niter++) {
            var shape_index = niter + 2 * vertex_index;
            var p0 = (shape_index == 0 || shape_index == 3) ? this.bounds.pmin : this.bounds.pmax;
            var wall = (niter == 0) ? vertex.previous_wall(this, true, true) : vertex.next_wall(this, true);
            if (wall != this && wall != null && wall.valid && Math.abs(cn_dot(this.bounds.direction, wall.bounds.direction)) < CN_WALL_COSINE_SHAPE_THRESHOLD) {
                var check_vertex = (niter == 0) ? wall.vertices[1] : wall.vertices[0];
                var p1 = (check_vertex == vertex) ? wall.bounds.pmin : wall.bounds.pmax;
                const shp = cn_intersect_line(p0, this.bounds.direction, p1, wall.bounds.direction);
                if (shp) this.shape[shape_index] = shp;
            }
        }
        return false;
    }

    /**
     * Build opening data
     */
    update_openings() {
        this.opening_area = [0, this.bounds.length];
        this.wall_interfaces.forEach(wi => wi.openings = []);

        this.openings.forEach(opening => {
            opening.parent = this;
            opening.update_position();
        });

        //*** check openings positions
        if (this.wall_type.free) {
            this.openings.forEach(opening => opening.valid = false);
            return;
        }

        //*** Compute valid opening area */
        var zz = [0, 0, 0, 0];
        for (var niter = 0; niter < 4; niter++) {
            var z = this.compute_abcissa(this.shape[niter]);
            zz[niter] = z;
            if (niter < 2) {
                if (z > this.opening_area[0])
                    this.opening_area[0] = z;
            } else {
                if (z < this.opening_area[1])
                    this.opening_area[1] = z;
            }
        }

        //*** Apply to openings */
        this.openings.sort(sort_openings_by_position);
        for (var i = 0; i < this.openings.length; i++) {
            var opening = this.openings[i];
            opening.valid = true;
            if (opening.position < this.opening_area[0] - 0.001 || opening.position + opening.get_width() > this.opening_area[1] + 0.001) {
                opening.valid = false;
            }
        }

        //*** Attach openigns to interfaces */
        this.openings.filter(opening => opening.valid).forEach(opening => {
            const x0 = opening.position;
            const x1 = x0 + opening.get_width();
            opening.wall_interface = null;;
            let best_length = 0;
            this.wall_interfaces.forEach(wi => {
                if (wi.wall_positions[0] <= x1 &&
                    wi.wall_positions[1] >= x0) {
                    wi.openings.push(opening);
                    let length = 0;
                    length += 2 * (x1 - x0);
                    if (x0 < wi.wall_positions[0]) length -= wi.wall_positions[0] - x0;
                    if (x1 > wi.wall_positions[1]) length -= x1 - wi.wall_positions[1];
                    if (!opening.wall_interface || best_length < length) {
                        opening.wall_interface = wi;
                        best_length = length;
                    }
                }
            });
            if (!opening.wall_interface) opening.valid = false;
        });

        /*** check overlapping */
        for (var i = 0; i < this.openings.length; i++) {
            var opening = this.openings[i];
            if (!opening.valid) continue;
            //*** check intersection with other opening */
            for (var j = 0; j < i; j++) {
                let other_opening = this.openings[j];
                if (!other_opening.valid) continue;
                if (opening.overlaps(other_opening)) {
                    opening.valid = false;
                    break;
                }
            }
        }

        //*** compute free characteristic of interfaces */
        if (this.balcony || this.wall_type.free)
            this.wall_interfaces.forEach(wi => wi.free = true);
        else
            this.wall_interfaces.forEach(wi => wi.free = wi.openings.some(op => op.valid && op.opening_type.free));

        this.update_niches();
    }

    /**
     * Build opening data
     */
    update_niches() {
        this.niches.forEach(niche => {
            niche.wall = this;
            niche.update_position();
        });

        //*** No niches in free or curtain walls
        if (this.wall_type.free || this.is_curtain_wall()) {
            this.niches.forEach(niche => niche.valid = false);
            return;
        }

        //*** Apply to niches */
        this.niches.sort(sort_niches_by_position);
        for (var i = 0; i < this.niches.length; i++) {
            var niche = this.niches[i];
            niche.valid = true;
            if (niche.wall_position < this.opening_area[0] - 0.001 || niche.wall_position + niche.width > this.opening_area[1] + 0.001) {
                niche.valid = false;
                continue;
            }

            if (this.openings.filter(p => p.valid).find(opening => niche.overlaps(opening))) {
                niche.valid = false;
                continue;
            }

            //*** check intersection with other niche */
            for (var j = 0; j < i; j++) {
                let other_niche = this.niches[j];
                if (!other_niche.valid) continue;
                if (!other_niche.wall_side != niche.wall_side) continue;
                if (niche.overlaps(other_niche)) {
                    niche.valid = false;
                    break;
                }
            }
        }

        //*** Attach openigns to interfaces */
        this.niches.filter(niche => niche.valid).forEach(niche => {
            const x0 = niche.wall_position;
            const x1 = x0 + niche.width;
            niche.wall_interface = null;;
            let best_length = 0;
            this.wall_interfaces.forEach(wi => {
                if (wi.wall_positions[0] <= x1 &&
                    wi.wall_positions[1] >= x0) {
                    wi.niches.push(niche);
                    let length = 0;
                    length += 2 * (x1 - x0);
                    if (x0 < wi.wall_positions[0]) length -= wi.wall_positions[0] - x0;
                    if (x1 > wi.wall_positions[1]) length -= x1 - wi.wall_positions[1];
                    if (!niche.wall_interface || best_length < length) {
                        niche.wall_interface = wi;
                        best_length = length;
                    }
                }
            });
            if (!niche.wall_interface) niche.valid = false;
        });
    }

    is_curtain_wall() {
        return this.wall_type.constructor == cn_wall_type && this.wall_type.category == 'curtain';
    }

    has_facings() {
        if (this.wall_type.free) return false;
        if (this.wall_type.constructor == cn_wall_type)
            return !this.is_curtain_wall();
        return (this.wall_type.category == 'wall' || this.wall_type.category == 'planter' || this.wall_type.category == 'fence');
    }

    optimize_position(element, position, precision) {

        //*** initial position of the opening */
        let element_box = element.get_vertical_box();
        let width = element_box.size[0];
        var x = this.compute_abcissa(position) - 0.5 * width;
        if (x < this.opening_area[0]) x = this.opening_area[0];
        else if (x + width > this.opening_area[1]) x = this.opening_area[1] - width;

        //*** Which are the elements we must check ?  */
        let other_elements = this.openings.filter(op => op.valid && op != element);
        let forbidden_side = (element.constructor == cn_niche) ? 1 - element.wall_side : -1;
        other_elements = other_elements.concat(this.niches.filter(n => n.valid && n != element && n.wall_side != forbidden_side));
        let other_boxes = other_elements.map(e => e.get_vertical_box());

        //*** maybe snap ? */
        other_boxes.forEach(b => {
            if (Math.abs(x - b.posmin[0] - b.size[0]) < precision)
                x = b.posmin[0] + b.size[0];
            else if (Math.abs(x + width - b.posmin[0]) < precision)
                x = b.posmin[0] - width;
        });
        element_box.posmin[0] = x + 0.001;
        element_box.enlarge_distance(-0.001);
        if (other_boxes.find(b => b.intersects(element_box))) return false;
        return x;
    }

    //***********************************************************************************
    //**** Draw the wall in svg
    //***********************************************************************************
    draw(camera, add_classes = [], fill = '', highlighted_space_segments = []) {
        var html = '';
        if (!this.valid) return html;

        if (camera.background_mode) {
            return camera.draw_polygon(this.footprint, "blueprint");
        }

        var p0 = camera.world_to_screen(this.vertex_position(0));
        var p1 = camera.world_to_screen(this.vertex_position(1));

        var base_class = (this.balcony) ? 'balcony' : 'wall';

        //*** Special case for mouseover or selection : only draw highlight */
        const selected = (add_classes.indexOf('selected') >= 0);
        const mouseover = (add_classes.indexOf('mouseover') >= 0);
        if (selected || mouseover) {
            html += `<line class="wall_highlight ${(selected) ? 'selected' : 'mouseover'}" x1="${p0[0]}" y1="${p0[1]}" x2="${p1[0]}" y2="${p1[1]}" />`;

            var axis_class = base_class + '_axis';
            if (this.locked) axis_class += ' locked';
            html += '<line class=\'' + axis_class + '\'' + ' ' + fill + ' x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' />';

            return html;
        }

        if (this.status < 0)
            html += '<g opacity=\'0.3\'>';

        var exp = (add_classes.indexOf('exp') >= 0);

        if (this.wall_type.free) {
            html += '<line class=\'' + base_class + '_free ' + add_classes.join(' ') + ' ' + fill
                + '\' x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' />';
            if (this.status < 0)
                html += '</g>';
            return html;
        }

        var draw_class = base_class;
        var extra_classes = '';
        if (add_classes) {
            extra_classes = ' ' + add_classes.join(' ');
            draw_class += extra_classes;
        }
        if (this.locked) extra_classes += ' locked';
        if (this.shape.length == 4) {
            if (camera.show_wall_type && !this.balcony) {
                this.wall_interfaces.forEach(wi => html += wi.draw_wall_type(camera));
                /*var w = camera.world_to_screen_scale * this.wall_type.thickness;
                var h = w * 10;
                html += '<pattern id=\'' + this.wall_type.ID + camera.ID + '\' x=\'0\' y=\'0\' width=\'' + w + '\' height=\'' + h + '\' patternUnits=\'userSpaceOnUse\' viewBox=\'0 0 ' + w + ' ' + h + '\'>';
                html += this.wall_type.draw_svg_icon(w, h, true);
                html += '</pattern>';

                var or = this.bounds.pmin;
                var dx = this.bounds.normal;
                let flow_direction = this.get_flow_direction();
                if (this.flip_wall_type) flow_direction = !flow_direction;
                if (flow_direction) {
                    or = this.bounds.pmax;
                    dx = cn_mul(this.bounds.normal, -1);
                }
                var ort = camera.world_to_screen(or);
                var dy = this.bounds.direction;

                // @ts-ignore
                function transform(pt) {
                    var pt1 = cn_sub(pt, or);
                    return [camera.world_to_screen_scale * cn_dot(dx, pt1), camera.world_to_screen_scale * cn_dot(dy, pt1)];
                }

                var angle = cn_polar(this.bounds.direction)[1] * 180 / Math.PI;

                var path = '';
                if (flow_direction)
                    path += '<g transform=\'translate(' + ort[0] + ',' + ort[1] + ') rotate(' + (-90 - angle) + ') scale(-1,1)\'>';
                else
                    path += '<g transform=\'translate(' + ort[0] + ',' + ort[1] + ') rotate(' + (-90 - angle) + ')\'>';
                //
                path += '<path d=\'';
                var s = transform(this.shape[0]);
                path += ' M ' + s[0] + ' ' + s[1];
                s = transform(this.vertices[0].position);
                path += ' L ' + s[0] + ' ' + s[1];
                s = transform(this.shape[1]);
                path += ' ' + s[0] + ' ' + s[1];
                s = transform(this.shape[2]);
                path += ' ' + s[0] + ' ' + s[1];
                s = transform(this.vertices[1].position);
                path += ' ' + s[0] + ' ' + s[1];
                s = transform(this.shape[3]);
                path += ' ' + s[0] + ' ' + s[1] + 'Z\' fill=\'url(#' + this.wall_type.ID + camera.ID + ')\'/>';

                path += '</g>';

                html += path;*/
            }

            html += camera.draw_path([this.shape[0], this.vertex_position(0), this.shape[1], this.shape[2], this.vertex_position(1), this.shape[3]], draw_class, true, fill);

            //*** Draw contour of the wall */
            const contour_class = `${base_class}_line${extra_classes}`;
            if (this.footprint)
                html += camera.draw_polygon(this.footprint, contour_class);
            else {
                html += camera.draw_path([this.shape[0], this.shape[3]], contour_class);
                html += camera.draw_path([this.shape[1], this.shape[2]], contour_class);
            }
        }

        //*** Draw wall interfaces */
        //this.wall_interfaces.forEach(wi => html += camera.draw_polygon(wi.footprint, "roof_dormer selected mouseover"));

        //*** Draw wall axis */
        if (!exp) {
            var axis_class = base_class + '_axis';
            if (this.locked) axis_class += ' locked';
            html += '<line class=\'' + axis_class + '\'' + ' ' + fill + ' x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' />';
        }

        if (highlighted_space_segments.length) {
            this.space_segments.forEach(ss => {
                const targetedHss = highlighted_space_segments.find(hss => hss.ID === ss.ID);
                if (targetedHss) {
                    html += ss.draw(camera, [], targetedHss.style);
                }
            })
        }

        this.space_segments.forEach(ss => {
            html += ss.draw_substrate(camera, exp ? ['exp'] : []);
        });

        if (this.status < 0)
            html += '</g>';

        return html;
    }

    //***********************************************************************************
    //**** Draw the wall shape in svg
    //***********************************************************************************
    draw_shape(camera, draw_class) {
        var html = '';
        if (this.shape.length != 4) return html;

        var p0 = camera.world_to_screen(this.vertex_position(0));
        var p1 = camera.world_to_screen(this.vertex_position(1));

        html += '<path class=\'' + draw_class + '\' d=\'';
        var s0 = camera.world_to_screen(this.shape[0]);
        var s1 = camera.world_to_screen(this.shape[1]);
        var s2 = camera.world_to_screen(this.shape[2]);
        var s3 = camera.world_to_screen(this.shape[3]);
        html += ' M ' + s0[0] + ' ' + s0[1];
        html += ' L ' + p0[0] + ' ' + p0[1];
        html += ' ' + s1[0] + ' ' + s1[1];
        html += ' ' + s2[0] + ' ' + s2[1];
        html += ' ' + p1[0] + ' ' + p1[1];
        html += ' ' + s3[0] + ' ' + s3[1] + 'Z\' />';

        return html;
    }

    //***********************************************************************************
    //**** Project a point on the wall.
    //***********************************************************************************
    project(p) {
        return cn_project_segment(p, this.vertex_position(0), this.vertex_position(1));
    }

    //***********************************************************************************
    //**** returns -1 if no intersection with wall, 0 if on first vertex, 1 if on second vertex, 2 if on wall.
    //***********************************************************************************
    intersects(p, range) {
        const v0 = this.vertex_position(0);
        const v1 = this.vertex_position(1);
        var direction = cn_sub(v1, v0);
        var length = cn_normalize(direction);
        if (length < 0.01) return false;
        var d = cn_sub(p, v0);
        var x = cn_dot(direction, d);
        if (x < -range) return -1;
        if (x > length + range) return -1;

        var normal = cn_normal(direction);
        var z = cn_dot(normal, d);
        if (this.axis == CN_OUTER) z += this.wall_type.thickness * 0.5;
        else if (this.axis == CN_INNER) z -= this.wall_type.thickness * 0.5;
        if (Math.abs(z) > this.wall_type.thickness * 0.5 + range) return -1;
        if (x < range) return 0;
        if (x > length - range) return 1;
        return 2;
    }

    //***********************************************************************************
    //**** Returns true if wall contains the point
    //***********************************************************************************
    contains(p, tolerance = 0) {
        if (typeof (this.bounds) == 'undefined') this.build_self();

        //*** Check bounding box */
        if (!this.get_bounding_box().contains_point(p, tolerance)) return false;

        //*** check y position */
        const y0 = cn_dot(this.bounds.normal, cn_sub(p, cn_middle(this.bounds.pmin, this.bounds.pmax)));
        const y = Math.abs(y0) - 0.5 * this.wall_type.thickness;
        if (y > tolerance) return false;
        let max_distance = tolerance - y;

        //*** Special case for thin walls */
        const a = this.compute_abcissa(p);
        if (this.wall_type.thickness < 0.001) {
            if (a < -tolerance || a > this.bounds.length + tolerance) return false;
            if (a < 0 && tolerance + a > max_distance) max_distance = tolerance + a;
            if (a > this.bounds.length && tolerance - a + this.bounds.length > max_distance) max_distance = tolerance - a + this.bounds.length;
            return max_distance;
        }

        //*** Compute x position */
        let s1 = y0 / this.wall_type.thickness + 0.5;
        if (s1 > 1) s1 = 1;
        else if (s1 < 0) s1 = 0;
        const a0 = this.compute_abcissa(cn_add(cn_mul(this.shape[0], 1 - s1), cn_mul(this.shape[1], s1)));
        const a1 = this.compute_abcissa(cn_add(cn_mul(this.shape[3], 1 - s1), cn_mul(this.shape[2], s1)));
        if (a < a0 - tolerance || a > a1 + tolerance) return false;
        if (a < a0 && tolerance - a0 + a > max_distance) max_distance = tolerance - a0 + a;
        if (a > a1 && tolerance - a + a1 > max_distance) max_distance = tolerance - a + a1;

        return max_distance;
    }

    //***********************************************************************************
    //**** Project a point on the wall.
    //***********************************************************************************
    snap(p, camera) {
        const v0 = this.vertex_position(0);
        const v1 = this.vertex_position(1);
        var direction = cn_sub(v1, v0);
        var length = cn_normalize(direction);
        if (length < camera.snap_world_distance) {
            var vtx = null;
            var min_distance = 0;
            for (var k = 0; k < 2; k++) {
                const vk = (k == 0) ? v0 : v1;
                if (Math.abs(p[0] - vk[0]) > camera.snap_world_distance) continue;
                if (Math.abs(p[1] - vk[1]) > camera.snap_world_distance) continue;
                var dst = cn_dist(p, vk);
                if (vtx && dst > min_distance) continue;
                vtx = this.vertices[k];
                min_distance = dst;
            }
            if (vtx == null) return -1;
            return min_distance;
        }

        var normal = cn_normal(direction);
        var d0 = cn_sub(p, v0);

        var pscal = cn_dot(direction, d0);
        if (pscal <= -camera.snap_world_distance) return -1;
        if (pscal >= length + camera.snap_world_distance) return -1;
        var z = Math.abs(cn_dot(normal, d0));
        if (z > this.wall_type.thickness * 0.5 && z > camera.snap_world_distance) return -1;
        return z;
    }

    //***********************************************************************************
    //**** Split the wall
    //***********************************************************************************
    /**
     * Splits the wall with given vertex.
     * New wall is returned.
     * @param {cn_vertex} vertex
     * @param {cn_transaction_manager} transaction_manager
     * @returns {cn_wall}
     */
    split(vertex, transaction_manager = null) {

        var wall = new cn_wall(vertex, this.vertices[1], this.wall_type, this.axis, this.scene);
        wall.balcony = this.balcony;
        wall.fence = this.fence;
        wall.space_segments = this.space_segments;
        wall.flip_wall_type = this.flip_wall_type;
        if (transaction_manager)
            transaction_manager.push_item_set(this.scene, "walls");
        this.scene.walls.push(wall);

        if (transaction_manager)
            transaction_manager.push_item_set(this, ["vertices", "openings"]);
        this.vertices[1] = vertex;

        //*** move openings
        var openings = this.openings;
        this.openings = [];
        var length = cn_dist(this.vertices[0].position, this.vertices[1].position);
        openings.forEach(opening => {
            if (opening.position < length)
                this.openings.push(opening);
            else {
                if (transaction_manager)
                    transaction_manager.push_item_set(opening, ["wall", "position"]);
                opening.wall = wall;
                opening.position -= length;
                wall.openings.push(opening);
            }
        });

        return wall;
    }

    /**
     * Returns true if walls are parallel
     * @param {cn_wall} other_wall
     * @returns {boolean}
     */
    parallel_to(other_wall) {
        return Math.abs(cn_dot(this.bounds.direction, other_wall.bounds.direction)) > CN_WALL_COSINE_SHAPE_THRESHOLD;
    }
    //******************************************************
    //*** get other vertex
    //******************************************************
    other_vertex(v) {
        if (this.vertices[0] == v) return this.vertices[1];
        return this.vertices[0];
    }

    other_vertex_position(v) {
        if (this.vertices[0] == v) return this.vertex_position(1);
        return this.vertex_position(0);
    }

    //******************************************************
    //*** find opening
    //******************************************************
    find_opening(p) {
        if (this.openings.length == 0) return null;
        var z = cn_dot(this.bounds.direction, cn_sub(p, this.bounds.pmin));
        for (var i in this.openings) {
            if (z < this.openings[i].position) continue;
            if (z > this.openings[i].position + this.openings[i].get_width()) continue;
            return this.openings[i];
        }
        return null;
    }

    /**
     * Find space segment for a given point
     * @param {number} wall_side
     * @param {number} abcissa
     * @returns {import('./cn_space_segment').cn_space_segment}
     */
    find_space_segment(wall_side, abcissa) {
        return this.space_segments.find(ss => {
            if (ss.wall_side != wall_side) return false;
            if (wall_side >= 2) return true;
            if (ss.wall_positions[0] < ss.wall_positions[1])
                return (abcissa >= ss.wall_positions[0] && abcissa <= ss.wall_positions[1]);
            return (abcissa <= ss.wall_positions[0] && abcissa >= ss.wall_positions[1]);
        });
    }

    //******************************************************
    //*** find opening before a given position
    //******************************************************
    opening_before(pos) {
        if (this.openings.length == 0) return null;
        var xmax = 0;
        var opening = null;
        for (var i in this.openings) {
            var x = this.openings[i].position + this.openings[i].get_width();
            if (x >= pos) continue;
            if (opening && x < xmax) continue;
            opening = this.openings[i];
            xmax = x;
        }
        return opening;
    }

    opening_after(pos) {
        if (this.openings.length == 0) return null;
        var xmin = 0;
        var opening = null;
        for (var i in this.openings) {
            var x = this.openings[i].position;
            if (x <= pos) continue;
            if (opening && x > xmin) continue;
            opening = this.openings[i];
            xmin = x;
        }
        return opening;
    }

    //******************************************************
    //*** does this wall cross other wall ?
    //******************************************************
    crosses(walls) {
        var p0 = this.vertex_position(0);
        var d0 = this.bounds.direction;
        for (var i in walls) {
            var w = walls[i];
            if (w == this) continue;
            if (w.vertices[0] == this.vertices[0]) continue;
            if (w.vertices[0] == this.vertices[1]) continue;
            if (w.vertices[1] == this.vertices[0]) continue;
            if (w.vertices[1] == this.vertices[1]) continue;

            var dis = cn_dot(d0, w.bounds.normal);
            if (Math.abs(dis) < 0.01) {
                continue;
            }

            var x = cn_dot(w.bounds.normal, cn_sub(w.vertex_position(0), p0)) / dis;
            if (x < -0.1) continue;
            if (x > this.bounds.length + 0.1) continue;
            var pt = cn_add(p0, cn_mul(d0, x));
            var x1 = cn_dot(w.bounds.direction, cn_sub(pt, w.vertex_position(0)));
            if (x1 < -0.1) continue;
            if (x1 > w.bounds.length + 0.1) continue;
            return true;
        }
        return false;
    }

    //***********************************************************************************
    //**** returns true if wall is lossy
    //***********************************************************************************
    is_lossy(storey = null) {
        if (this.is_wall_dormer()) return true;
        return this.wall_interfaces.find(wi => wi.is_lossy(storey));
    }

    //***********************************************************************************
    /**
     * Returns true if heat flows go from space 0 to space 1, false otherwise.
     * Returns true if not defined
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {boolean}
     */
    get_flow_direction(storey = null) {
        if (this.is_wall_dormer()) return true;

        let is_true = 0;
        let is_false = 0;
        this.wall_interfaces.forEach(wi => {
            const fd = wi.get_flow_direction();
            const l = wi.space_segments[0].size + wi.space_segments[1].size;
            if (fd)
                is_true += l
            else
                is_false += l;
        });
        return is_true >= is_false;
    }

    //***********************************************************************************
    //**** returns true if wall can have limit conditions
    //***********************************************************************************
    /**
     * returns true of wall is partially an outer wall
     */
    is_outer(storey = null) {
        return this.wall_interfaces.find(wi => !wi.spaces[0].is_roof(storey) || !wi.spaces[1].is_roof(storey));
    }

    /**
     * returns true of wall is partially an inner wall
     */
    is_inner(storey = null) {
        return this.wall_interfaces.find(wi => wi.spaces[0].is_roof(storey) && wi.spaces[1].is_roof(storey));
    }

    /**
     * Returns spaces on each side, if this is the same space along the full side.
     */
    get_common_spaces() {
        if (this.wall_interfaces.length == 0) return [null, null];
        let spaces = [this.wall_interfaces[0].spaces[0], this.wall_interfaces[0].spaces[1]];
        for (var side = 0; side < 2; side++) {
            for (var i = 1; i < this.wall_interfaces.length; i++) {
                if (this.wall_interfaces[i].spaces[side] != spaces[side]) {
                    spaces[side] = null;
                    break;
                }
            }
        }
        return spaces;
    }

    /**
     * returns wall interface at given position
     * @param {number} abcissa
     * @param {number} side
     * @returns {cn_wall_interface}
     */
    get_wall_interface(abcissa, side) {
        return this.wall_interfaces.find(wi => {
            let a0 = this.compute_abcissa(wi.shape[(side == 0) ? 0 : 1]);
            if (a0 > abcissa) return false;
            let a1 = this.compute_abcissa(wi.shape[(side == 0) ? 3 : 2]);
            return a1 <= abcissa;
        });
    }
    //***********************************************************************************
    //**** raytrace to wall axis
    //***********************************************************************************
    /**
     *
     * @param {*} origin
     * @param {*} direction
     * @param {*} max_distance
     * @returns {{distance: number, point: number[], wall; cn_wall, wall_position: number, wall_side: number}}
     */
    raytrace(origin, direction, max_distance) {
        var x = cn_dot(direction, this.bounds.normal);
        if (Math.abs(x) < 0.0001) return false;
        var lambda = cn_dot(this.bounds.normal, cn_sub(this.vertices[0].position, origin)) / x;
        if (lambda <= 0) return false;
        if (lambda >= max_distance) return false;
        var p = cn_add(origin, cn_mul(direction, lambda));
        var e = cn_dot(this.bounds.direction, cn_sub(p, this.vertices[0].position));
        if (e < 0) return false;
        if (e > this.bounds.length) return false;
        const space_index = (x > 0) ? 0 : 1;
        return {
            'distance': lambda,
            'point': cn_add(this.vertices[0].position, cn_mul(this.bounds.direction, e)),
            'wall': this,
            'wall_position': e,
            'wall_side': space_index,
            'spaces': [null, null]
        };
    }

    //***********************************************************************************
    //**** raytrace to shape
    //***********************************************************************************

    /**
     * Raytrace to shape
     * @param {number[]} origin
     * @param {number[]} direction
     * @param {number} max_distance
     * @returns {cn_wall_shape_impact}
     */
    raytrace_shape(origin, direction, max_distance = 10000) {
        let impact = null;
        const sides = [2, 1, 3, 0];
        for (var k = 0; k < 4; k++) {
            const p0 = this.shape[k];
            const p1 = this.shape[(k + 1) % 4];
            const dir = cn_sub(p1, p0);
            const l = cn_normalize(dir);
            if (l < 0.001) continue;
            const normal = cn_normal(dir);
            const pscal = cn_dot(normal, direction);
            if (Math.abs(pscal) < 0.0001) continue;
            const x = cn_dot(normal, cn_sub(p0, origin)) / pscal;
            if (x <= 0 || x >= max_distance) continue;
            if (impact && x >= impact.distance) continue;
            const point = cn_add(origin, cn_mul(direction, x));
            const y = cn_dot(dir, cn_sub(point, p0));
            if (y < 0 || y > l) continue;
            impact = new cn_wall_shape_impact(this, sides[k], x, point, normal, this.compute_abcissa(point));
        }
        return impact;
    }

    /**
     * Raytrace to support shape = same as above, but skip tops that are not free
     * @param {number[]} origin
     * @param {number[]} direction
     * @returns {cn_wall_shape_impact}
     */
    raytrace_support_shape(origin, direction) {
        const impact = this.raytrace_shape(origin, direction);
        if (impact && impact.side > 1) {
            if (!this.vertices[impact.side - 2].is_free()) return null;
        }
        return impact;
    }

    /**
     * Finds on which side lies a point.
     * This method assumes that the points lies on the shape.
     * Cannot return top sides if they are not free
     * @param {number[]} point
     * @returns {number}
     */
    find_support_wall_side(point) {
        const y = cn_dot(this.bounds.normal, cn_sub(point, cn_middle(this.bounds.pmin, this.bounds.pmax)));
        let res = (y > 0) ? 1 : 0;
        if (this.wall_type.thickness == 0 || (!this.vertices[0].is_free() && !this.vertices[1].is_free()))
            return res;
        let min_distance = Math.abs(Math.abs(y) - 0.5 * this.wall_type.thickness);
        for (var nv = 0; nv < 2; nv++) {
            if (this.vertices[nv].is_free()) {
                const p0 = this.shape[2 * nv];
                const p1 = this.shape[2 * nv + 1];
                const dir = cn_sub(p1, p0);
                if (cn_normalize(dir) > 0.001) {
                    const xx = Math.abs(cn_dot(cn_normal(dir), cn_sub(p0, point)));
                    if (xx < min_distance) {
                        min_distance = xx;
                        res = 2 + nv;
                    }
                }
            }
        }
        return res;
    }

    //***********************************************************************************
    //***Reverse the wall */
    reverse() {
        if (this.axis > 0)
            this.axis = 3 - this.axis;
        for (var i in this.openings)
            this.openings[i].reverse();
    }

    /**
     * Accept element visitor
     *
     * @param {cn_element_visitor} element_visitor
     */
    accept_visitor(element_visitor) {
        element_visitor.visit_wall(this);
    }

    /**
     * Builds a list of 3D vertices (at given height z) of the wall basis
     * @param {number} z
     * @returns {number[][]}
     */
    build_3d_vertices(z) {
        var vertices = [];
        vertices.push(cn_clone_3d(this.shape[0], z));
        vertices.push(cn_clone_3d(this.shape[3], z));
        vertices.push(cn_clone_3d(this.shape[2], z));
        vertices.push(cn_clone_3d(this.shape[1], z));
        return vertices;
    }

    /**
     * Builds a polygon that forms the wall basis
     * @param {number} z
     * @returns {fh_polygon}
     */
    build_3d_polygon(z) {
        if (this.wall_type.free || this.wall_type.thickness < 0.001) return null;
        var polygon = new fh_polygon([0, 0, z], [0, 0, 1]);
        polygon.add_contour(this.build_3d_vertices(z));
        polygon.compute_contours();
        return polygon;
    }

    /**
     * Builds half the wall basis, corresponding to given input side.
     * @param {number} z
     * @param {number} side
     * @returns {fh_polygon}
     */
    build_3d_half_polygon(z, side) {
        if (this.wall_type.free || this.wall_type.thickness < 0.001) return null;
        var vertices = this.build_3d_vertices(z);
        var nor = (side == 0) ? this.bounds.normal : cn_mul(this.bounds.normal, -1);
        var p0 = cn_add(this.shape[0], cn_mul(this.bounds.normal, 0.5 * this.wall_type.thickness()))
        var x0 = cn_dot(nor, p0);
        var kept_vertices = [];
        var pprec = vertices[vertices.length - 1];
        var xprec = cn_dot(nor, pprec) - x0;
        for (var n = 0; n < vertices.length; n++) {
            var x = cn_dot(nor, vertices[n]) - x0;
            if (x * xprec < 0) {
                const a0 = x / (x - xprec);
                const a1 = -xprec / (x - xprec);
                kept_vertices.push(cn_add(cn_mul(pprec, a0), cn_mul(vertices[n], a1)));
            }
            if (x < 0)
                kept_vertices.push(cn_clone(vertices[n]));

            xprec = x;
            pprec = vertices[n];
        }
        kept_vertices.forEach(v => v.push(z));
        var polygon = new fh_polygon([0, 0, z], [0, 0, 1]);
        polygon.add_contour(kept_vertices);
        polygon.compute_contours();
        return polygon;
    }


    /**
     * Compute next measure point, on the side of the wall.
     * @param {number} start_position
     * @param {number} side
     * @param {boolean} forward
     * @param {boolean} stop_on_window
     * @param {any} output : filled by result
     * @return {number[]}
     */
    get_next_measure_point(start_position, side, forward, stop_on_window = false, output = null) {

        const p0 = (side == 0) ? this.bounds.pmin : this.bounds.pmax;
        var wall = this;
        var position = start_position;
        var nb_walls = 0;
        if (output) {
            output.walls = [];
            output.end = null;
        }
        while (true) {
            if (output) output.walls.push(wall);
            var p = (side == 0) ? wall.bounds.pmin : wall.bounds.pmax;

            //*** Maybe we need to stop on a window ?  */
            if (stop_on_window) {
                //*** along the wall or reverse ?  */
                if ((side == 0) == forward) {
                    for (var no = 0; no < wall.openings.length; no++) {
                        if (!wall.openings[no].valid) continue;
                        const x = wall.openings[no].position;
                        if (x < position) continue;
                        if (output) output.end = wall.openings[no];
                        return cn_add(p, cn_mul(wall.bounds.direction, x));
                    }
                } else {
                    for (var no = wall.openings.length - 1; no >= 0; no--) {
                        if (!wall.openings[no].valid) continue;
                        const x = wall.openings[no].position + wall.openings[no].get_width();
                        if (x > position) continue;
                        if (output) output.end = wall.openings[no];
                        return cn_add(p, cn_mul(wall.bounds.direction, x));
                    }
                }
            }

            //*** check next wall */
            const vertex_index = ((side == 0) == forward) ? 1 : 0;
            const next_wall = (forward) ? wall.vertices[vertex_index].next_wall(wall, false) : wall.vertices[vertex_index].previous_wall(wall, false);

            //*** dead end */
            if (next_wall == wall)
                return cn_clone(wall.shape[(forward) ? 3 - side * 2 : side * 2]);

            //*** compute next wall */
            var next_side = side;
            var next_forward = forward;
            if (next_wall.vertices[vertex_index] == wall.vertices[vertex_index])
                next_side = 1 - next_side;

            //*** maybe the wall side continues on another wall ? */
            var p1 = (next_side == 0) ? next_wall.bounds.pmin : next_wall.bounds.pmax;
            var p2 = cn_add(p1, cn_mul(next_wall.bounds.direction, next_wall.bounds.length));
            if (Math.abs(cn_dot(this.bounds.normal, cn_sub(p1, p0))) < 0.001 && Math.abs(cn_dot(this.bounds.normal, cn_sub(p2, p0))) < 0.001) {
                wall = next_wall;
                forward = next_forward;
                side = next_side;
                position = ((side == 0) == forward) ? 0 : wall.bounds.length;
                if (nb_walls > 100) {
                    logger.log('ERROR : too many walls');
                    return [0, 0];
                }
                nb_walls++;
                continue;
            }

            //*** we stop !
            if (output) output.end = next_wall;
            logger.log('stop on end', (forward) ? 3 - side * 2 : side * 2);
            return cn_clone(wall.shape[(forward) ? 3 - side * 2 : side * 2]);
        }
        return [0, 0];
    }

    /**
     * Compute measures of a wall.
     * - First dimension of the array is the side of the wall.
     * - Second dimension of the array is the position on the wall (start, end)
     * - output walls are the first walls out of the measure, or null in case of a dead end.
     * @returns {{points:number[][][],walls:cn_wall[][]}}
     */
    get_measures() {
        var tmp_output = {};
        var output = {
            points: [[], []],
            walls: [[], []]
        };

        for (var side = 0; side < 2; side++) {
            output.points[side].push(this.get_next_measure_point(this.bounds.length / 2, side, false, false, tmp_output));
            output.walls[side].push(tmp_output.end);
            output.points[side].push(this.get_next_measure_point(this.bounds.length / 2, side, true, false, tmp_output));
            output.walls[side].push(tmp_output.end);
        }
        return output;
    }

    get_measures_from_vertex(v) {
        const measures = [];
        const vertex_index = this.vertices.indexOf(v);
        if (vertex_index >= 0) {
            const direction = (vertex_index == 0) ? this.bounds.direction : cn_mul(this.bounds.direction, -1);
            for (var side = 0; side < 2; side++) {
                const m = { points: [this.shape[(side == 0) ? 0 : 2], this.shape[(side == 0) ? 3 : 1]], direction: cn_mul(direction, -1) };
                measures.push(m);
                const side_dir = (side == 0) ? this.bounds.direction : cn_mul(this.bounds.direction, -1);
                let xx = m.points.map(mp => cn_dot(mp, side_dir));
                const f = (vertex_index == side) ? 1 : 0;
                this.supported_vertices.filter(sv => sv.walls.length == 1 && sv.support_wall_side == side).forEach(sv => {
                    const shi = (sv.walls[0].vertices[0] == sv) ? 0 : 2;
                    for (var k = 0; k < 2; k++) {
                        const p = sv.walls[0].shape[shi + k]
                        const x = cn_dot(p, side_dir);
                        if (x > xx[0] && x < xx[1]) {
                            xx[f] = x;
                            m.points[f] = p;
                        }
                    }
                });
            }
        }
        else if (v.support_wall == this && v.support_wall_side < 2 && v.walls.length == 1) {
            const side = v.support_wall_side;
            for (var vi = 0; vi < 2; vi++) {
                const direction = (side == 0) ? this.bounds.direction : cn_mul(this.bounds.direction, -1);
                const m = { points: [this.shape[(side == 0) ? 0 : 2], this.shape[(side == 0) ? 3 : 1]], direction: (vi) ? cn_mul(direction, -1) : direction };
                measures.push(m);
                const side_dir = (side == 0) ? this.bounds.direction : cn_mul(this.bounds.direction, -1);
                let xx = m.points.map(mp => cn_dot(mp, side_dir));

                let f = 1 - vi;
                const shi = (v.walls[0].vertices[0] == v) ? 0 : 2;
                for (var k = 0; k < 2; k++) {
                    const p = v.walls[0].shape[shi + k];
                    const x = cn_dot(p, side_dir);
                    if (x > xx[0] && x < xx[1]) {
                        xx[f] = x;
                        m.points[f] = p;
                    }
                }

                f = vi;
                this.supported_vertices.filter(sv => sv != v && sv.walls.length == 1 && sv.support_wall_side == side).forEach(sv => {
                    const shi = (sv.walls[0].vertices[0] == sv) ? 0 : 2;
                    for (var k = 0; k < 2; k++) {
                        const p = sv.walls[0].shape[shi + k]
                        const x = cn_dot(p, side_dir);
                        if (x > xx[0] && x < xx[1]) {
                            xx[f] = x;
                            m.points[f] = p;
                        }
                    }
                });
            }
        }
        return measures;
    }

    /**
     * Returns the facing trimmings that are potentially on the wall
     * @param {number} side
     * @returns {Array<import('./cn_facing_trimming').cn_facing_trimming>}
     */
    _get_trimmings(side) {
        const wall_point = (side == 0) ? this.bounds.pmin : this.bounds.pmax;
        var wall_normal = this.bounds.normal;
        if (side == 0) wall_normal = cn_mul(wall_normal, -1);

        function trimming_filter(f) {
            if (f.wall == null) return false;
            if (cn_dot(f.wall_normal, wall_normal) < 0.999) return false;
            if (Math.abs(cn_dot(wall_normal, cn_sub(f.wall_point, wall_point))) > 0.05) return false;
            return true;
        };
        return this.scene.building.facing_trimmings.filter(ft => trimming_filter(ft));
    }

    /**
     * Remove all valide openings of the wall from the polygon
     * @param {fh_polygon} polygon
     * @param {import('./cn_storey').cn_storey} storey
     */
    remove_openings_from_polygon(polygon, storey) {
        var p0 = cnx_clone(this.bounds.pmin);
        const dx = cnx_clone(this.bounds.direction);
        const dy = cnx_clone(this.bounds.normal);
        const dz = [0, 0, 1];

        //*** Apply openings */
        this.openings.forEach(opening => {
            if (opening.valid) {
                p0[2] = opening.compute_offset();
                const p = cnx_add(p0, cnx_mul(dx, opening.position));
                const piercing = opening.get_opening_type().build_piercing_polygon(p, dx, dy, dz);
                polygon.substracts(piercing);
            }
        });
    }

    /**
     * Remove all valide niches of the wall from the polygon
     * @param {fh_polygon} polygon
     * @param {number} side
     */
    remove_niches_from_polygon(polygon, side) {
        this.niches.filter(niche => niche.valid && niche.wall_side == side).forEach(niche => {
            let piercing = niche.build_piercing_polygon();
            if (piercing)
                polygon.substracts(piercing);
        });
    }

    /**
     * Returns the length of the wall, dependig its side
     * @param {number} side
     * @returns {number}
     */
    get_length(side) {
        if (side == 0)
            return cn_dist(this.shape[0], this.shape[3]);
        return cn_dist(this.shape[1], this.shape[2]);
    }

    get_bounding_box() {
        const box = new cn_box();
        this.shape.forEach(s => box.enlarge_point(s));
        return box;
    }

    /**
     * returns true if wall connexion to vertices hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_vertex_geometry(date) {
        if (this.get_date('wall_type') > date) return false;
        if (this.get_date('axis') > date) return false;
        if (this.vertices[0].get_date('position') > date) return false;
        if (this.vertices[1].get_date('position') > date) return false;
        if (!this.wall_type.up_to_date_thickness(date)) return false;
        return true;
    }

    /**
     * returns true if wall geometry hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_geometry(date) {
        if (!this.up_to_date_vertex_geometry(date)) return false;
        if (!this.vertices[0].up_to_date_geometry(date)) return false;
        if (!this.vertices[1].up_to_date_geometry(date)) return false;
        return true;
    }

    /**
     * Builds the 3D shape of the wall
     * @param {import('./cn_storey').cn_storey} storey
     * @param {boolean} remove_openings
     * @returns {fh_solid}
     */
    build_3d_solid(storey, remove_openings = true) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            var solid_wall = new fh_solid();

            let roof_volume = null;
            if (!storey.exterior)
                roof_volume = (this.dormer) ? this.dormer.get_clipping_volume(0) : storey.build_space_volume();

            let zmin = 0;
            let zmax = 0;

            if (storey.exterior) {
                zmax = this.wall_type.wall_height;
            }
            else if (roof_volume) {
                const box = roof_volume.get_bounding_box();
                zmin = box.position[2];
                zmax = box.position[2] + box.size[2];
            }

            var footprint = this.footprint.clone();
            cn_translate_polygon(footprint, [0, 0, zmin]);
            solid_wall.extrusion(footprint, [0, 0, zmax - zmin]);

            //*** Add openings
            if (remove_openings && this.openings.length > 0) {
                //*** data for hole geometry
                var dx = [this.bounds.direction[0], this.bounds.direction[1], 0];
                var dy = [-this.bounds.direction[1], this.bounds.direction[0], 0];
                var dz = [0, 0, 1];
                var p0 = [this.bounds.pmin[0], this.bounds.pmin[1], 0];
                p0 = fh_sub(p0, fh_mul(dy, 0.01));

                //*** loop on openings
                this.openings.filter(op => op.valid).forEach(opening => {
                    p0[2] = opening.compute_offset();
                    //*** add opening hole
                    var opening_solid = opening.get_opening_type().build_piercing_solid(fh_add(p0, fh_mul(dx, opening.position)), dx, dy, dz, this.wall_type.thickness + 0.02);
                    solid_wall.substracts(opening_solid);
                });
            }
            solid_wall.intersects(roof_volume);

            return solid_wall;
        }, (remove_openings) ? "solid_pierced" : "solid_unpierced", storey);
    }

    /**
     * Returns true if the wall has several upper wall types, or if connected to such a wall.
     * @returns {boolean}
     */
    has_verticality() {
        if (this.upper_wall_types.length > 0) return true;
        return this.vertices.some(v => (v.support_wall && v.support_wall.upper_wall_types.length) || v.walls.some(w => w.upper_wall_types.length > 0));
    }

    /** Returns a lsit of dummy walls that represent the verticality of the wall and / or its vertex connections.
     * Each section is a dummy wall, with appropriate shape, and additional parameters:
     * - _z0 = start height of section (-1000 if first section)
     * - _z1 = end height of section (1000 if last section)
     * - _nu = upper part index in the range [0, this.upper_wall_types.length]
     * @returns {Array<cn_wall>}
    */
    get_dummy_walls() {
        // @ts-ignore
        return this.get_lazy_data(() => {
            //***maybe no verticality ?
            if (!this.has_verticality()) return [this];

            let upper_walls = [];
            for (var nu = 0; nu <= this.upper_wall_types.length; nu++) {
                let wall_type = (nu == 0) ? this.wall_type : this.upper_wall_types[nu - 1];
                let uw0 = this.get_neighbour_upper_parts(0, nu);
                let uw1 = this.get_neighbour_upper_parts(1, nu);
                let i0 = 0;
                let i1 = 0;
                let z0 = (nu == 0) ? -1000 : this.upper_heights[nu - 1];
                let zmax = (nu < this.upper_wall_types.length) ? this.upper_heights[nu] : 1000;
                while (i0 < uw0.length && i1 < uw1.length) {
                    let u0 = uw0[i0];
                    let u1 = uw1[i1];
                    let z1 = Math.min(u0.z1, u1.z1);
                    if (z1 >= u0.z1) i0++;
                    if (z1 >= u1.z1) i1++;
                    z1 = Math.min(z1, zmax);
                    if (z1 > z0) {
                        let this_wall = this.dummy_clone(wall_type);
                        this_wall.build_self();
                        let wtt = [u0.wall_type, u1.wall_type];
                        for (var nv = 0; nv < 2; nv++) {
                            if (!wtt[nv]) continue;
                            let vertex = this_wall.vertices[nv];
                            if (vertex.support_wall) {
                                vertex.support_wall = vertex.support_wall.dummy_clone(wtt[nv]);
                                vertex.support_wall.build_self();
                            }
                            else if (vertex.walls.length == 2) {
                                let owi = 1 - vertex.walls.indexOf(this_wall);
                                let other_wall = vertex.walls[owi];
                                let vi = other_wall.vertices.indexOf(this.vertices[nv]);
                                let ww = other_wall.dummy_clone(wtt[nv]);
                                ww.vertices[vi] = vertex;
                                vertex.walls[owi] = ww;
                                ww.build_self();
                            }
                            this_wall.adjust_shape(nv);
                        }
                        this_wall["_z0"] = z0;
                        this_wall["_z1"] = z1;
                        this_wall["_nu"] = nu;
                        upper_walls.push(this_wall);
                        z0 = z1;
                    }
                }
            }
            return upper_walls;
        }, "dummy_walls");
    }

    get_thinnest_dummy_wall() {
        let upper_walls = this.get_dummy_walls();
        /** @type {cn_wall} */
        let thinnest = this;
        upper_walls.forEach(uw => {
            if (uw.wall_type.thickness < thinnest.wall_type.thickness)
                thinnest = uw;
        });
        return thinnest;
    }

    compute_facing_count() {
        this.space_segments.forEach(ss => ss.compute_facing_count());
        this.openings.forEach(op => op.compute_facing_count());
        this.niches.forEach(nch => nch.compute_facing_count());
    }

}

